/** The P9 solo advance control (screens §P9): "Continue to FY{…}" (`soloAdvance nextRound`), or "See your final" after the last round. */
import { useState } from "react";
import type { ErrorBody } from "@bsim/contracts";
import type { RouteCall } from "../api/types";
import { useSoloAdvance } from "./useSoloAdvance";
import { navigate } from "../shell/history";
import { GxErrorBanner } from "../theme/primitives";
import { playPath } from "./run";

export function SoloAdvance({ runId, roundId, label, soloAdvance, pendingLabel, beside = false, compact = false, testId = "solo.advance" }: {
  /** A second copy of the control on one page (the foot of Results) names itself apart from the first. */ readonly testId?: string;
  readonly runId: string; readonly roundId: string; readonly label: string; readonly soloAdvance: RouteCall<"soloAdvance">;
  /** Read while the next round is being prepared ("Opening FY2027…"). */ readonly pendingLabel?: string;
  /** In a page-head action row: the normal button height. */ readonly compact?: boolean;
  /** Sits beside a primary button ("See results"): the secondary of the same family, not a second large accent. */ readonly beside?: boolean;
}) {
  const { pending, go: advance } = useSoloAdvance(runId, soloAdvance);
  const [error, setError] = useState<ErrorBody["error"] | null>(null);
  // Once the year has advanced the button stays off until the page leaves: the old round's Continue offers a step that is gone, so a second click would only raise "That step isn't available".
  const go = async () => {
    if (pending) return;
    // The next year opens on the Outlook, then the decisions: the player comes to Decide informed on the economy.
    const r = await advance({ action: "nextRound", fromRoundId: roundId }, (next) => navigate(next.phase === "deciding" ? `/c/${runId}/outlook` : playPath(next)));
    if (!r.ok) setError(r.error);
  };
  return (
    <div className="gx-solo-advance">
      {error === null ? null : <GxErrorBanner error={error} />}
      <button type="button" className={beside ? "gx-btn gx-btn--secondary" : compact ? "gx-btn gx-btn--accent" : "gx-btn gx-btn--accent gx-btn--lg"} data-testid={testId} aria-busy={pending} disabled={pending} onClick={() => void go()}>{pending ? (pendingLabel ?? `${/^Continue to /.test(label) ? label.replace(/^Continue to /, "Starting ") : "Opening your final"}…`) : `${label} →`}</button>
    </div>
  );
}
